import ActionButtonRounded from 'Components/ActionButtonRounded';
import DataTable from 'Components/DataTable';
import HistoryRowSubHeader from 'Components/HistoryRowSubHeader';
import InputHandler from 'Components/InputHandler';
import { formatTime, isPartialDate, sortPartialDate } from 'neuro-utils';
import { formatPartialDate } from 'Utility/date';
import * as React from 'react';
import { useIntl } from 'react-intl';
import colors from '../../../../config/theme/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import {
  formatOptionIndent,
  formatSeizureClassification,
  getOwnSeizureTypesClassifications,
  GroupHeader,
} from 'Routes/Seizure/utils';
import { actions } from 'Store/myapp/actions';
import { useDispatch } from 'react-redux';
import { MenuItem, Dialog, Paper, DialogActions, DialogContent, DialogTitle } from '@mui/material';
import { dialogActions, dialogCancel, dialogContent, dialogTitle } from '../../../../config/theme/componentTheme';
import { isString } from 'Utility/typeGuards';
import { seizureType } from 'neuro-schemas';
import { RenderAlerts } from 'Components/Alerts';

const allClassifications = seizureType.utils.classificationOptions as TSeizureClassification[];

const PatientReportedSeizuresHistory = ({ documents, seizureTypes }: IOwnProps) => {
  const { formatMessage } = useIntl();
  const fm = (id: string) => formatMessage({ id });

  const uniqueSeizureNames = Array.from(new Set(documents.map(d => d.seizureName))).filter(
    (name): name is string => typeof name === 'string',
  );
  const [classificationFilterInput, setClassificationFilterInput] = React.useState<string>('all');
  const [selectedDocs, setSelectedDocs] = React.useState<Array<string>>([]);
  const [selectListValue, setSelectListValue] = React.useState<boolean>(false);
  const [listedDocs, setListedDocs] = React.useState<Array<IDocumentWithTypeV2<'patientReportedSeizure'>>>(
    documents.filter(d => !d.status),
  );
  const [listedDocsHistoryData, setListedDocsHistoryData] = React.useState<
    (string | number | React.JSX.Element | (string | number | string[] | React.JSX.Element | number[])[])[]
  >([]);
  const [rejectDialogOpen, setRejectDialogOpen] = React.useState<boolean>(false);

  const controlOpts = ['other', 'return'];
  const controlOptsOG = ['other'];
  const originalSet = getOwnSeizureTypesClassifications();
  const [classificationOpts, setClassificationOpts] = React.useState<string[]>(originalSet.concat(controlOptsOG));
  const [classificationMenuOpen, setClassificationMenuOpen] = React.useState<string | undefined>(undefined);
  const [tempClassifications, setTempClassifications] = React.useState<
    Array<{ [key: string]: TSeizureClassification | null | undefined }>
  >(listedDocs.map(d => ({ [d._id]: d.seizureType ?? undefined })));

  const classificationWithHeaders: string[] = [];
  classificationOpts.forEach((clas, i) => {
    // Add headers to all top level classifications that have children
    if (clas.length === 3 && classificationOpts[i + 1]?.match('^' + clas)) {
      classificationWithHeaders.push('header' + clas);
    }
    classificationWithHeaders.push(clas);
  });

  const onCloseClassification = (e: React.BaseSyntheticEvent): void => {
    if (e.target.dataset.value === 'other' || e.target.dataset.value === 'return') return;
    setClassificationMenuOpen(undefined);
  };

  const onChangeSelection = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    const docId = name.split('@')[1];
    if (!docId) return;
    if (value) {
      setSelectedDocs(prev => [...prev, docId]);
      const selectedDoc = documents.find(doc => doc._id === docId);
      if (selectedDoc?.seizureType === undefined || selectedDoc.seizureType === null) {
        const defaultSeizClassification = seizureTypes.find(st => st.nameByPatient === selectedDoc?.seizureName)
          ?.classification?.classification;
        if (defaultSeizClassification) {
          onChangeSeizureType({ [`seizureType@${docId}`]: defaultSeizClassification });
        }
      }
    } else {
      setSelectedDocs(prev => prev.filter(id => id !== docId));
    }
  };

  const onChangeClassificationFilter = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if (typeof value === 'string') {
      setSelectedDocs([]);
      setSelectListValue(false);
      setClassificationFilterInput(value);
    }
  };

  const onChangeSeizureType = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    const docId = name.split('@')[1];
    if (!docId) return;

    if (isString(value) && controlOpts.includes(value)) {
      switch (value) {
        case 'other':
          setClassificationOpts([...allClassifications, 'return']);
          break;
        case 'return':
          setClassificationOpts([...originalSet, 'other']);
          break;
      }
    } else {
      setTempClassifications(prev => {
        const docIdIndex = prev.findIndex(p => Object.keys(p)[0] === docId);
        if (docIdIndex === -1) {
          return [...prev, { [docId]: value as TSeizureClassification }];
        } else {
          prev.splice(docIdIndex, 1, { [docId]: value as TSeizureClassification });
          return prev;
        }
      });
    }
  };

  const [updating, setUpdating] = React.useState<boolean>(false);
  const dispatch = useDispatch();

  const handleAcceptClick = async () => {
    setUpdating(true);
    for (const id of selectedDocs) {
      const oldData = documents.find(d => d._id === id);
      const seizureTypeThisDoc =
        tempClassifications.find(td => Object.keys(td)[0] === oldData?._id)?.[id] ?? oldData?.seizureType;

      // Only allow accepting/confirming seizures if seizureType has been selected
      if (seizureTypeThisDoc) {
        await actions.putNewCommit(
          'patientReportedSeizure',
          id,
          {
            ...oldData,
            status: 'accepted',
            seizureType: seizureTypeThisDoc,
          },
          dispatch,
        );
      }
    }
    setSelectedDocs([]);
    setUpdating(false);
  };

  const handleRejectClick = async () => {
    setUpdating(true);
    for (const id of selectedDocs) {
      const oldData = documents.find(d => d._id === id);
      await actions.putNewCommit(
        'patientReportedSeizure',
        id,
        {
          ...oldData,
          status: 'rejected',
          seizureType:
            tempClassifications.find(td => Object.keys(td)[0] === oldData?._id)?.[id] ?? oldData?.seizureType,
        },
        dispatch,
      );
    }
    setSelectedDocs([]);
    setUpdating(false);
    setRejectDialogOpen(false);
  };

  React.useEffect(() => {
    setListedDocs(
      documents.filter(
        d => !d.status && (classificationFilterInput === 'all' ? true : classificationFilterInput === d.seizureName),
      ),
    );
  }, [documents]);

  React.useEffect(() => {
    listedDocs.forEach(d => {
      onChangeSelection({ [`selected@${d._id}`]: selectListValue });
    });
  }, [selectListValue]);

  React.useEffect(() => {
    if (classificationFilterInput === 'all') {
      setListedDocs(documents.filter(d => !d.status));
    } else {
      setListedDocs(documents.filter(d => d.seizureName === classificationFilterInput && !d.status));
    }
  }, [classificationFilterInput]);

  React.useEffect(() => {
    setTempClassifications(prev => {
      const newListed = listedDocs
        .map(d => ({ [d._id]: d.seizureType }))
        .filter(ld => !prev.find(p => Object.keys(p)[0] === Object.keys(ld)[0]));
      return [...prev, ...newListed];
    });
  }, [listedDocs]);

  React.useEffect(() => {
    setListedDocsHistoryData(
      listedDocs
        .sort((d1, d2) => sortPartialDate(d2.startDate, d1.startDate))
        .map((d, i) => [
          <div key={i} style={{ display: 'flex', marginTop: '0.9rem' }}>
            <InputHandler
              type={'CheckboxSingle'}
              name={`selected@${d._id}`}
              editing={true}
              formData={{
                document: { [`selected@${d._id}`]: selectedDocs.includes(d._id) },
                onChange: onChangeSelection,
              }}
              option="true"
              showLabel={false}
              style={{ margin: 0 }}
            />
          </div>,
          <div key={d._docCreateDate} style={{ paddingRight: '1rem' }}>
            <div style={{ fontWeight: 600, display: 'inline-block', whiteSpace: 'nowrap' }}>
              {formatPartialDate(d.startDate)}
              {d.endDate && !d.startTime ? '\u00A0–\u00A0' : d.startTime ? '\u00A0' : ''}
            </div>
            <div
              style={{
                fontWeight: d.endDate && isPartialDate(d.endDate) ? 600 : 400,
                display: 'inline-block',
                whiteSpace: 'nowrap',
              }}
            >
              {d.endDate ? `${formatPartialDate(d.endDate)}` : d.startTime ? `${formatTime(d.startTime)}` : ''}
            </div>
          </div>,
          d.count ?? '-',
          <div key={d._cdate}>
            <div style={{ fontWeight: 600 }}>{d.seizureName ?? fm('patientReportedSeizure.unNamed')}</div>
            <div>
              <InputHandler
                name={`seizureType@${d._id}`}
                type="Select"
                editing={selectedDocs.includes(d._id)}
                formData={{
                  document: {
                    [`seizureType@${d._id}`]: selectedDocs.includes(d._id)
                      ? tempClassifications.find(c => Object.keys(c)[0] === d._id)?.[d._id]
                      : listedDocs.find(ld => ld._id === d._id)?.seizureType,
                  },
                  onChange: onChangeSeizureType,
                }}
                options={classificationWithHeaders}
                optionFormatter={(o): string | React.JSX.Element => {
                  return controlOpts.includes(`${o}`) ? (
                    <span style={{ fontWeight: 400 }} data-value={o}>
                      {fm(`seizure.${o}`)}
                    </span>
                  ) : (
                    <span style={{ fontWeight: 400 }}>
                      {classificationMenuOpen &&
                      allClassifications.every(c => classificationOpts.includes(c) && selectedDocs.includes(d._id))
                        ? formatOptionIndent(`${o} ${fm(`seizure.opts.${o}`)}`)
                        : selectedDocs.includes(d._id)
                          ? formatSeizureClassification(o as TSeizureClassification, 'short')
                          : formatSeizureClassification(o as TSeizureClassification, 'long')}
                    </span>
                  );
                }}
                menuItemComponent={(o: string | number, optFormatter) => {
                  const option = `${o}`;
                  if (option.substring(0, 6) === 'header') {
                    // Different menuitems for group headers
                    return (
                      <GroupHeader
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          e.preventDefault();
                        }}
                      >
                        <MenuItem disabled>
                          {optFormatter ? optFormatter(option.substring(6)) : option.substring(6)}
                        </MenuItem>
                      </GroupHeader>
                    );
                  }
                  return (
                    <MenuItem value={option} key={option}>
                      {optFormatter ? (
                        <div style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                          {optFormatter(option)}
                        </div>
                      ) : (
                        option
                      )}
                    </MenuItem>
                  );
                }}
                width={40}
                minWidth={18}
                open={classificationMenuOpen === d._id}
                onOpen={(): void => setClassificationMenuOpen(d._id)}
                onClose={onCloseClassification}
                placeholder={fm('seizure.classificationPlaceholder')}
              />
            </div>
          </div>,
          d.medicCompliance === 'yes' ? (
            <div key={'medicCompliance_yes_' + d._id} style={{ display: 'flex' }}>
              <CheckCircleIcon style={{ marginRight: '0.5rem', color: colors.success }} />
              {fm('general.yes')}
            </div>
          ) : d.medicCompliance === 'no' ? (
            <div key={'medicCompliance_no_' + d._id} style={{ display: 'flex' }}>
              <WarningIcon style={{ marginRight: '0.5rem', color: colors.warning.dark }} />
              {fm('general.no')}
            </div>
          ) : (
            '-'
          ),
          d.freeDescription ?? '-',
        ]),
    );
  }, [tempClassifications, selectedDocs, classificationMenuOpen, classificationOpts]);

  const seizureTypesWithoutClassification = seizureTypes.filter(st => !st.classification?.classification);

  return (
    <div>
      <HistoryRowSubHeader header={fm('patientReportedSeizure.title')} />
      {seizureTypesWithoutClassification.length > 0 && (
        <div>
          <RenderAlerts
            alerts={[
              { id: 'seizureTypesWithoutClassification', textID: `seizure.seizureTypesAlert`, severity: 'info' },
            ]}
            elevation={0}
            width={80}
          />
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginBottom: '2rem' }}>
        <div
          style={{
            backgroundColor: colors.myms.primary,
            color: colors.white,
            fontSize: 14,
            alignItems: 'center',
            justifyContent: 'center',
            paddingLeft: '0.5rem',
            paddingRight: '0.5rem',
            textAlign: 'center',
            minWidth: '1rem',
            marginRight: '1rem',
            borderRadius: 10,
          }}
        >
          {documents.filter(d => !d.status).length}
        </div>
        <div style={{ color: colors.tertiaryText }}>{fm('patientReportedSeizure.unhandledReports')}</div>
      </div>
      <div
        style={{
          width: '100%',
          height: '5.6rem',
          backgroundColor: colors.appBlue.lightest,
          borderRadius: '0.5rem',
          display: 'flex',
          alignItems: 'center',
          marginBottom: '2rem',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginRight: '6rem' }}>
          <span style={{ color: colors.appBlue.default, paddingLeft: '2rem', marginRight: '1rem' }}>
            {fm('patientReportedSeizure.classificationFilter')}
          </span>
          <div>
            <InputHandler
              type="Select"
              name="classificationFilter"
              editing={true}
              options={['all', ...uniqueSeizureNames]}
              optionFormatter={o => (o === 'all' ? fm('patientReportedSeizure.all') : `${o}`)}
              formData={{
                onChange: onChangeClassificationFilter,
                document: { classificationFilter: classificationFilterInput },
              }}
            />
          </div>
        </div>
      </div>
      <DataTable
        headers={[
          <div key="headerCheckbox" style={{ marginTop: '0.9rem' }}>
            <InputHandler
              type={'CheckboxSingle'}
              name={'selectList'}
              editing={true}
              formData={{
                document: { selectList: selectListValue },
                onChange: () => setSelectListValue(!selectListValue),
              }}
              option="true"
              showLabel={false}
              style={{ margin: 0 }}
            />
          </div>,
          'patientReportedSeizure.timeframe',
          <div key="count" style={{ whiteSpace: 'pre-line' }}>
            {fm('patientReportedSeizure.count').replace(' ', '\n')}
          </div>,
          <div key="seizureNameSeizureType" style={{ whiteSpace: 'nowrap' }}>
            {fm('patientReportedSeizure.seizureNameSeizureType')}
          </div>,
          <div key="medicCompliance" style={{ whiteSpace: 'pre-line' }}>
            {fm('patientReportedSeizure.medicCompliance').replace(' ', '\n')}
          </div>,
          'patientReportedSeizure.freeDescription',
        ]}
        headersFormatter={fm}
        data={{
          rowData: listedDocsHistoryData,
        }}
      />
      <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginTop: '2rem' }}>
        <div style={{ marginRight: '2rem' }}>
          <ActionButtonRounded
            text={'patientReportedSeizure.rejectSelected'}
            width={15}
            height={4}
            fontSize={16}
            onClick={() => setRejectDialogOpen(true)}
            disabled={selectedDocs.length < 1}
            loading={updating}
          />
        </div>
        <div>
          <ActionButtonRounded
            text={'patientReportedSeizure.acceptSelected'}
            width={15}
            height={4}
            fontSize={16}
            filled
            onClick={handleAcceptClick}
            disabled={
              selectedDocs.length < 1 ||
              documents
                .filter(d => selectedDocs.includes(d._id))
                .some(d => {
                  const classification =
                    tempClassifications.find(td => Object.keys(td)[0] === d._id)?.[d._id] ?? d.seizureType;

                  return !classification;
                })
            }
            loading={updating}
            disabledTooltip={
              selectedDocs.length > 0 ? <>{fm('seizure.onlySeizuresWithTypeCanBeConfirmed')}</> : undefined
            }
          />
        </div>
      </div>
      {rejectDialogOpen && (
        <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} PaperComponent={Paper}>
          <DialogTitle style={dialogTitle}>{fm('patientReportedSeizure.rejectSeizures')}</DialogTitle>
          <DialogContent style={dialogContent}>
            <div>{fm('patientReportedSeizure.confirmRejecting')}</div>
          </DialogContent>
          <DialogActions style={dialogActions}>
            <button style={{ all: 'unset', ...dialogCancel }} onClick={() => setRejectDialogOpen(false)}>
              {fm('general.cancel')}
            </button>
            <ActionButtonRounded
              text={'patientReportedSeizure.rejectSelected'}
              onClick={handleRejectClick}
              width={15}
              height={4}
              fontSize={16}
              loading={updating}
            />
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<'patientReportedSeizure'>>;
  seizureTypes: Array<IDocumentWithTypeV2<'seizureType'>>;
}

export default PatientReportedSeizuresHistory;
